import { useState, type KeyboardEvent, type PointerEvent } from "react";

import { TAU, arcPath, ringPoint as point } from "@/lib/donut";

export type DonutSlice = {
  id: string;
  name: string;
  /** Label colour from Google Calendar's palette; undefined until loaded. */
  hex?: string;
  percent: number;
};

type Props = {
  /** Shares that sum to 100 (the caller keeps that invariant). */
  slices: DonutSlice[];
  /** Called with the full list of new percents, same order as slices. */
  onChange: (percents: number[]) => void;
  size?: number;
};

/**
 * Interactive donut for the Ideal Day mix. Each Label is a slice and the
 * slices always fill the ring. Drag the handle between two slices to move
 * share from one to the other; the handle at the top trades between the
 * last and the first slice. Handles are keyboard sliders too: arrow keys
 * move 1%, with Shift 5%.
 */
export function IdealDayDonut({ slices, onChange, size = 260 }: Props) {
  const [hover, setHover] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);

  const c = size / 2;
  const handleRadius = 8;
  const outer = c - handleRadius - 2;
  const inner = outer * 0.62;
  const mid = (outer + inner) / 2;

  const n = slices.length;
  const percents = slices.map((s) => Math.max(0, s.percent));

  // cum[k] is where slice k ends.
  const cum: number[] = [];
  percents.reduce((acc, p) => {
    cum.push(acc + p);
    return acc + p;
  }, 0);

  /**
   * Move boundary k (end of slice k, start of slice k+1 — wrapping to
   * slice 0 for the last one) to an absolute ring position.
   */
  function moveBoundary(k: number, target: number) {
    if (n < 2) return;
    const j = (k + 1) % n;
    const start = cum[k] - percents[k];
    const end = cum[k] + percents[j];
    const next = Math.round(Math.min(end, Math.max(start, target)));
    if (next === cum[k]) return;
    const out = [...percents];
    out[k] = next - start;
    out[j] = end - next;
    onChange(out);
  }

  function pointerPercent(ev: PointerEvent<SVGCircleElement>, k: number): number {
    const svg = ev.currentTarget.ownerSVGElement;
    if (!svg) return cum[k];
    const rect = svg.getBoundingClientRect();
    const dx = ev.clientX - (rect.left + rect.width / 2);
    const dy = ev.clientY - (rect.top + rect.height / 2);
    const raw = (((Math.atan2(dy, dx) + Math.PI / 2) / TAU) * 100 + 100) % 100;
    // Pick the wrap of raw nearest the handle so dragging past 12 o'clock
    // does not jump.
    const candidates = [raw - 100, raw, raw + 100];
    return candidates.reduce((best, v) =>
      Math.abs(v - cum[k]) < Math.abs(best - cum[k]) ? v : best,
    );
  }

  function onHandleDown(ev: PointerEvent<SVGCircleElement>, k: number) {
    ev.preventDefault();
    ev.currentTarget.setPointerCapture(ev.pointerId);
    setDragging(k);
  }

  function onHandleMove(ev: PointerEvent<SVGCircleElement>, k: number) {
    if (dragging !== k) return;
    moveBoundary(k, pointerPercent(ev, k));
  }

  function onHandleUp(ev: PointerEvent<SVGCircleElement>) {
    ev.currentTarget.releasePointerCapture(ev.pointerId);
    setDragging(null);
  }

  function onHandleKey(ev: KeyboardEvent<SVGCircleElement>, k: number) {
    const step =
      ev.key === "ArrowRight" || ev.key === "ArrowDown"
        ? 1
        : ev.key === "ArrowLeft" || ev.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0) return;
    ev.preventDefault();
    moveBoundary(k, cum[k] + step * (ev.shiftKey ? 5 : 1));
  }

  const centreTitle = hover !== null ? slices[hover].name : "Ideal Day";
  const centreValue = hover !== null ? `${percents[hover]}%` : "100%";

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      style={{ maxWidth: size, touchAction: "none" }}
      role="img"
      aria-label={`Ideal Day: ${slices.map((s) => `${s.name} ${s.percent}%`).join(", ")}`}
    >
      {/* Ring background so an empty mix still reads as a ring. */}
      <circle
        cx={c}
        cy={c}
        r={mid}
        fill="none"
        strokeWidth={outer - inner}
        className="stroke-muted"
      />

      {slices.map((slice, i) => {
        const from = cum[i] - percents[i];
        const to = cum[i];
        if (percents[i] <= 0) return null;
        const colour = slice.hex ?? "var(--muted-foreground)";
        const hoverProps = {
          opacity: hover !== null && hover !== i ? 0.55 : 1,
          onMouseEnter: () => setHover(i),
          onMouseLeave: () => setHover(null),
        };
        const title = <title>{`${slice.name} ${percents[i]}%`}</title>;
        // A single 100% slice has no boundary to draw an arc between, so it
        // is a stroked ring instead.
        return percents[i] >= 100 ? (
          <circle
            key={slice.id}
            cx={c}
            cy={c}
            r={mid}
            fill="none"
            stroke={colour}
            strokeWidth={outer - inner}
            {...hoverProps}
          >
            {title}
          </circle>
        ) : (
          <path
            key={slice.id}
            d={arcPath(c, outer, inner, from, to)}
            fill={colour}
            stroke="var(--card)"
            strokeWidth={2}
            {...hoverProps}
          >
            {title}
          </path>
        );
      })}

      <text
        x={c}
        y={c - 4}
        textAnchor="middle"
        className="fill-foreground text-2xl font-semibold"
      >
        {centreValue}
      </text>
      <text
        x={c}
        y={c + 16}
        textAnchor="middle"
        className="fill-muted-foreground text-xs"
      >
        {centreTitle}
      </text>

      {n >= 2 &&
        cum.map((at, k) => {
          const [x, y] = point(c, mid, at % 100);
          const name = slices[k].name;
          const neighbour = slices[(k + 1) % n].name;
          return (
            <circle
              key={`handle-${slices[k].id}`}
              cx={x}
              cy={y}
              r={handleRadius}
              fill="var(--card)"
              stroke="var(--foreground)"
              strokeWidth={2}
              tabIndex={0}
              role="slider"
              aria-label={`Boundary between ${name} and ${neighbour}`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={at}
              aria-valuetext={`${name} ${percents[k]}%`}
              style={{ cursor: dragging === k ? "grabbing" : "grab" }}
              className="outline-none focus-visible:stroke-[3px] focus-visible:stroke-ring"
              onPointerDown={(ev) => onHandleDown(ev, k)}
              onPointerMove={(ev) => onHandleMove(ev, k)}
              onPointerUp={onHandleUp}
              onPointerCancel={onHandleUp}
              onKeyDown={(ev) => onHandleKey(ev, k)}
            />
          );
        })}
    </svg>
  );
}
